import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  memo,
  Fragment
} from 'react';
import { scaleLinear, line, extent, scalePoint } from 'd3';
import { useWindowSize } from '../../utils';
import { ChartWrapper, HiddenCircle, CircleLabel, Line } from './styles';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import { withTheme } from 'styled-components';
import LineChartLabels from './LineChartLabels';
import LineChartAxes from './LineChartAxes';
import PropTypes from 'prop-types';
import ChartTooltip from '../../components/ChartTooltip';
import { useTooltip } from '@visx/tooltip';
import { localPoint } from '@visx/event';

let tooltipTimeout;

const LineChart = ({
  data = {},
  labels,
  colors = [],
  padding = 50,
  emptyChartMessage,
  allowDecimalTicks = false,
  theme,
  showMarkers = true,
  pointContent,
  tickContent,
  lineWeight = 1
}) => {
  const svgRef = useRef(null);
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip();
  const { linesData = [], xAxisData = [] } = data;

  const [lines, setLines] = useState([]);
  const [markers, setMarkers] = useState();
  const [isOverlapping, setOverlapping] = useState({});

  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);

  const chartRef = useRef(null);

  const windowSize = useWindowSize(chartRef);

  const yDelta = useMemo(() => {
    const domain = extent(linesData.flat(), (d) => d);
    return domain[1] - domain[0] || 0;
  }, [linesData]);

  const xScale = useMemo(
    () =>
      scalePoint()
        .domain(xAxisData)
        .range([padding, chartWidth - padding]),
    [xAxisData, chartWidth, padding]
  );

  const yScale = useMemo(
    () =>
      scaleLinear()
        .domain(extent(linesData.flat(), (d) => d))
        .range([chartHeight - padding, padding]),
    [linesData, chartHeight, padding]
  );

  const defaultColors = useMemo(
    () =>
      theme
        ? [
            theme.colors.secondary,
            theme.colors.warning,
            theme.colors.error_bg,
            theme.colors.success_bg
          ]
        : [],
    [theme]
  );

  const getColor = useCallback(
    (index) => {
      const availableColors = colors.length ? colors : defaultColors;
      return availableColors[index % availableColors.length];
    },
    [colors, defaultColors]
  );

  const handleMouseLeave = useCallback(
    (event, marker) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);

      if (marker) {
        setOverlapping((prev) => ({
          ...prev,
          [marker.lineId]: false
        }));
      }
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, item) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(svgRef.current, event);
      showTooltip({
        tooltipData: {
          ...item,
          tick: false
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, svgRef.current]
  );

  const handleXAxisMouseOver = useCallback(
    (event, date) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(svgRef.current, event);
      const tickIndex = xAxisData.findIndex((d) => d === date);
      showTooltip({
        tooltipData: {
          label: date,
          tick: true,
          tickIndex
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, svgRef.current]
  );

  useEffect(() => {
    if (chartRef.current) {
      setChartWidth(chartRef.current.parentElement.offsetWidth);
      setChartHeight(chartRef.current.parentElement.offsetHeight);
    }
  }, [chartRef.current, windowSize]);

  useEffect(() => {
    if (!windowSize || !linesData.length) {
      return;
    }
    const lineGenerator = line()
      .x((d, i) => xScale(xAxisData[i]))
      .y((d) => yScale(d));

    setLines(linesData.map(lineGenerator));
    setMarkers(
      linesData.flat().map((d, i) => {
        const index = i % xAxisData.length;
        const lineId = Math.floor(i / xAxisData.length);
        return !showMarkers || index > 0
          ? {
              value: d,
              lineId,
              index,
              x: xScale(xAxisData[index]),
              y: yScale(d)
            }
          : undefined;
      })
    );
  }, [windowSize, linesData, xAxisData, xScale, yScale, showMarkers]);

  if (!linesData.length) {
    return (
      <ChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='alert'
            mainText='Sorry, no content.'
            smallText={emptyChartMessage}
            size='large'
          />
        </BoxV2>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper ref={chartRef}>
      {showMarkers && <LineChartLabels labels={labels} getColor={getColor} />}
      <svg ref={svgRef} width={chartWidth} height={chartHeight}>
        <LineChartAxes
          highlightTicks={tickContent}
          chartWidth={chartWidth}
          chartHeight={chartHeight}
          padding={padding}
          xScale={xScale}
          yScale={yScale}
          yDelta={yDelta}
          allowDecimalTicks={allowDecimalTicks}
          rotateXAxisLabels={!showMarkers}
          onXAxisMouseOver={(event, date) => handleXAxisMouseOver(event, date)}
          onXAxisMouseLeave={(event) => handleMouseLeave(event)}
        />
        <g>
          {lines &&
            lines.map((line, i) => (
              <Fragment key={i}>
                {showMarkers ? (
                  <Line stroke={getColor(i)} d={line} data={linesData} />
                ) : (
                  <Line
                    stroke={getColor(i)}
                    strokeOpacity={isOverlapping[i] ? 1 : 0.5}
                    strokeWidth={isOverlapping[i] ? lineWeight + 2 : lineWeight}
                    d={line}
                    data={linesData}
                    onMouseOver={(e) => {
                      setOverlapping((prev) => ({
                        ...prev,
                        [i]: true
                      }));
                      handleMouseMove(e, {
                        label: labels[i],
                        color: getColor(i),
                        lineId: i
                      });
                    }}
                    onMouseLeave={(e) => {
                      setOverlapping((prev) => ({
                        ...prev,
                        [i]: false
                      }));
                      handleMouseLeave(e);
                    }}
                  />
                )}
              </Fragment>
            ))}
          {markers &&
            markers.map(
              (marker, i) =>
                marker && (
                  <Fragment key={i}>
                    {showMarkers ? (
                      <Fragment>
                        <circle
                          cx={marker.x}
                          cy={marker.y}
                          r={7.5}
                          stroke={getColor(marker.lineId)}
                          fill={getColor(marker.lineId)}
                        />
                        <CircleLabel x={marker.x} y={marker.y - 15}>
                          {marker.value}
                        </CircleLabel>
                      </Fragment>
                    ) : (
                      <HiddenCircle
                        cx={marker.x}
                        cy={marker.y}
                        r={7.5}
                        color={getColor(marker.lineId)}
                        onMouseMove={(e) => {
                          handleMouseMove(e, {
                            label: labels[marker.lineId],
                            color: getColor(marker.lineId),
                            lineId: marker.lineId,
                            index: marker.index
                          });
                          setOverlapping((prev) => ({
                            ...prev,
                            [marker.lineId]: true
                          }));
                        }}
                        onMouseLeave={(event) =>
                          handleMouseLeave(event, marker)
                        }
                      />
                    )}
                  </Fragment>
                )
            )}
        </g>
      </svg>
      {pointContent && tooltipOpen && !tooltipData.tick && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          {pointContent(tooltipData)}
        </ChartTooltip>
      )}
      {tickContent && tooltipOpen && tooltipData.tick && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          {tickContent(tooltipData)}
        </ChartTooltip>
      )}
    </ChartWrapper>
  );
};

LineChart.propTypes = {
  data: PropTypes.shape({
    linesData: PropTypes.array,
    xAxisData: PropTypes.array
  }),
  labels: PropTypes.array,
  colors: PropTypes.array,
  padding: PropTypes.number,
  emptyChartMessage: PropTypes.string,
  allowDecimalTicks: PropTypes.bool,
  showMarkers: PropTypes.bool,
  pointContent: PropTypes.func,
  tickContent: PropTypes.func,
  lineWeight: PropTypes.number
};

export default memo(withTheme(LineChart));
